import getElementsOutsideLimits from "../../../containers/domain/getElementsOutsideLimits";
import {isDeliveryValid} from "../../form/deliveryValidation";
import {isBillingValid} from "../../form/billingFormValidation";
import {removeElementsOutsideLimits} from "../../../features/plan/removeElementsOutsideLimitsThunk";
import {detectCollisions} from "../../../helpers/collisionDetector";
import {isElementCountValid, getInvalidAccessories} from "../../form/orderValidation";
import {
    getLidOpeningsArrangementMessages,
    getIntermediateCeilingOpeningsArrangementMessages
} from "./manholeArrangement";
import {Plan} from "types";
import {ValidationMessage} from "../../types";
import {getManholeOpeningDirectionErrorMessages} from "./manholeOpeningDirection";
import {getManholeCountErrorMessages} from "./manholeCount";
import {getLidHeightErrorMessages} from "./lidHeight";

export const validatePlan = (plan: Plan): ValidationMessage[] => {

    const elementsOutsideLimits = (): ValidationMessage[] => {
        const outside = getElementsOutsideLimits(plan as any) as any;

        return outside.some() ? [
            {
                message: 'Durch die Grössenänderung befinden sich Elemente ausserhalb des gültigen Bereiches! ' +
                    'Vergrössern Sie die Abmessungen oder löschen Sie die Elemente: ' +
                    [
                        ...(outside.vault && outside.vault.walls[0].some()
                            ? ['Ansicht A']
                            : []),
                        ...(outside.vault && outside.vault.walls[1].some()
                            ? ['Ansicht B']
                            : []),
                        ...(outside.vault && outside.vault.walls[2].some()
                            ? ['Ansicht C']
                            : []),
                        ...(outside.vault && outside.vault.walls[3].some()
                            ? ['Ansicht D']
                            : []),
                        ...(outside.lid && outside.lid.some() ? ['Abdeckung'] : []),
                        ...(outside.intermediateCeiling &&
                        outside.intermediateCeiling.some() ? ['Zwischendecke'] : []),
                        ...(outside.floor && outside.floor.some() ? ['Boden'] : []),
                    ]
                        .join(", "),
                resolveAction: () => removeElementsOutsideLimits(),
                resolveActionText: 'Elemente löschen'
            }
        ] : [];
    }

    const overlappingSleeves = (): ValidationMessage[] => {
        const walls = (plan?.vault?.walls ?? []) as any[];

        if (walls.length === 0) return [];
        const wallMeta = [
            {name: 'Ansicht A', location: '/step/2a', text: 'Zu Schritt 2a'},
            {name: 'Ansicht B', location: '/step/2b', text: 'Zu Schritt 2b'},
            {name: 'Ansicht C', location: '/step/2c', text: 'Zu Schritt 2c'},
            {name: 'Ansicht D', location: '/step/2d', text: 'Zu Schritt 2d'},
        ];

        return walls.slice(0, 4).flatMap((wall, idx) => {
            const collisionSets = detectCollisions(wall?.sleeves ?? []) as Set<string>;
            const hasOverlap = collisionSets.size > 0;
            if (!hasOverlap) return [];

            const {location, text} = wallMeta[idx] ?? {
                name: `Ansicht ${idx + 1}`,
                location: '/step/2a',
                text: 'Zu Schritt 2a',
            };

            return [{
                message:
                    `Ihre Konfiguration enthält Muffen, die sich überdecken. `
                    + `Die betreffenden Muffen wurden rot gekennzeichnet.`,
                resolveLocation: location,
                resolveLocationText: text,
            }];
        });
    };
    const insufficientWallWidthForSleeves = (): ValidationMessage[] => {
        const hasSleeves = () =>
            (plan as any).vault && (plan as any).vault.walls.some((w: any) => (w.sleeves?.length ?? 0) > 0)

        return plan.wallWidth < 15 && hasSleeves() ? [
            {
                message: 'Muffen setzten eine Wandstärke von 15 cm voraus! Erhöhen Sie die Wandstärke oder löschen ' +
                    'Sie die Muffen aus den Ansichten.'
            }
        ] : []
    }

    const maxClearMeasureForSubFloorLidExceeded = (): ValidationMessage[] => {
        const hasSubFloorLidWithManhole = () => plan.lid && plan.lid.type === 'subfloor' && plan.lid.manholes.length > 0
        const hasIntermediateCeiling = () => !!plan.intermediateCeiling
        const getManhole = () => plan.lid.manholes[0]
        const getVaultClearMeasure = () => getManhole().orientation === 'vertical' ? plan.width : plan.length
        const getManholeMeasure = () => getManhole().orientation === 'vertical'
            ? getManhole().height
            : getManhole().width

        return !hasSubFloorLidWithManhole() ||
        hasIntermediateCeiling() ||
        getManholeMeasure() >=
        getVaultClearMeasure() +
        20 ? [] : [
            {
                message: "Die Betonelemente der Unterflurabdeckung müssen die Wand mindestens 10 cm überlappen!" +
                    (getVaultClearMeasure() <= 130
                        ? ' Korrigieren Sie die Positionierung oder verwenden Sie das grössere Betonelement.'
                        : ' Verkleinern Sie den Schacht.')
            }
        ]
    }

    const coveredVaultWithoutOpening = (): ValidationMessage[] => {
        const hasLid = () => !!plan.lid
        const hasLidWithoutOpening = () => plan.lid &&
            plan.lid.manholes.length < 1 &&
            (plan.lid.blockOuts?.length ?? 0) < 1 &&
            (plan.lid.circularBlockOuts?.length ?? 0) < 1
        const hasIntermediateCeilingWithoutOpening = () => plan.intermediateCeiling &&
            ((plan.intermediateCeiling as any).circularBlockOuts?.length ?? 0) < 1 &&
            ((plan.intermediateCeiling as any).blockOuts?.length ?? 0) < 1

        return hasLidWithoutOpening() ? [
            {
                message: "Es wurde keine Einstiegsmöglichket definiert.",
                severity: 'error',
                resolveLocation: '/step/3',
                resolveLocationText: 'Zu Schritt 3'
            }
        ] : (!hasLid() && hasIntermediateCeilingWithoutOpening()) ? [
            {
                message: "Es wurde keine Einstiegsmöglichket definiert.",
                severity: 'info',
                resolveLocation: '/step/3',
                resolveLocationText: 'Zu Schritt 3'
            }
        ] : []
    }

    const subFloorNotFullyCovered = (): ValidationMessage[] => {
        if (!plan.lid ||
            plan.lid?.type !==
            'subfloor' ||
            !!plan.intermediateCeiling ||
            !plan.lid.manholes ||
            plan.lid.manholes.length !==
            1) return [];
        const manhole = plan.lid.manholes[0];
        const isManholeFullyCovered = manhole.x <=
            0 &&
            manhole.x +
            manhole.width >
            plan.length &&
            manhole.y <=
            0 &&
            manhole.y +
            manhole.height >
            plan.width;

        return isManholeFullyCovered ? [] : [
            {
                message: "Der Schacht ist nicht komplett abgedeckt.",
                severity: 'info',
                resolveLocation: '/step/3',
                resolveLocationText: 'Zu Schritt 3'
            }
        ]
    }

    const openingsOverlapOrMixedArrangement = (): ValidationMessage[] => [
        ...getLidOpeningsArrangementMessages(plan, {
            severity: 'error',
            resolveLocation: '/step/3',
            resolveLocationText: 'Zu Schritt 3'
        }),
        ...getIntermediateCeilingOpeningsArrangementMessages(plan, {
            severity: 'error',
            resolveLocation: '/step/3',
            resolveLocationText: 'Zu Schritt 3'
        }),
    ];

    const opposingManholeOpeningDirections = (): ValidationMessage[] => {
        return [
            ...getManholeOpeningDirectionErrorMessages(plan, {
                severity: 'error',
                resolveLocation: '/step/3',
                resolveLocationText: 'Zu Schritt 3'
            })
        ]
    }

    const manholeCount = (): ValidationMessage[] => {
        return [
            ...getManholeCountErrorMessages(plan, {
                severity: 'error',
                resolveLocation: '/step/3',
                resolveLocationText: 'Zu Schritt 3'
            })
        ]
    }

    const lidHeight = (): ValidationMessage[] => {
        return [
            ...getLidHeightErrorMessages(plan, {
                severity: 'error',
                resolveLocation: '/step/1',
                resolveLocationText: 'Zu Schritt 1'
            })
        ]
    }

    return [
        ...elementsOutsideLimits(),
        ...overlappingSleeves(),
        ...insufficientWallWidthForSleeves(),
        ...maxClearMeasureForSubFloorLidExceeded(),
        ...coveredVaultWithoutOpening(),
        ...subFloorNotFullyCovered(),
        ...openingsOverlapOrMixedArrangement(),
        ...opposingManholeOpeningDirections(),
        ...manholeCount(),
        ...lidHeight(),
    ]
}

export const validateProject = (plan: Plan, project: any): ValidationMessage[] => {
    const invalidDelivery = (): ValidationMessage[] => {
        return !isDeliveryValid(project) ? [
            {
                message: 'Bitte füllen Sie alle Pflichtfelder beim Schritt 6 (Lieferung) aus.',
                resolveLocation: '/step/6',
                resolveLocationText: 'Zu Schritt 6'
            }
        ] : []
    }

    const invalidOrder = (): ValidationMessage[] => {
        return !isElementCountValid(plan as any, project) ? [
            {
                message: 'Für eine Bestellung muss bei Menge mindestens ein Element ausgewählt werden.'
            }
        ] : []
    }

    const invalidAccessoryQuantities = (): ValidationMessage[] => {
        const invalidItems = getInvalidAccessories(project) as any[];
        if (invalidItems.length === 0) return [];

        const itemDetails = invalidItems
            .map((item: any) => `"${item.description}" (Mindestmenge: ${item.minOrderQuantity})`)
            .join(', ');


        return [{
            message: invalidItems.length > 1
                ? `Die folgenden Zubehörartikel unterschreiten die Mindestbestellmenge: ${itemDetails}. Bitte ` +
                `erhöhen Sie die Mengen oder entfernen Sie die Artikel.`
                : `Der folgende Zubehörartikel unterschreitet die Mindestbestellmenge: ${itemDetails}. Bitte ` +
                `erhöhen Sie die Menge oder entfernen Sie den Artikel.`
        }];
    };

    const invalidBilling = (): ValidationMessage[] => {
        return !isBillingValid(project) ? [
            {
                message: 'Bitte füllen Sie alle Pflichtfelder in diesem Schritt (Rechnungsadresse) aus.'
            }
        ] : []
    }

    const isPlanImagesDirty = (): ValidationMessage[] => {
        return (plan as any).dirty ? [
            {
                message: 'Bitte kontrollieren Sie nach der letzten Änderung den Plan in Schritt 5.',
                resolveLocation: '/step/5',
                resolveLocationText: 'Zu Schritt 5'
            }
        ] : [];
    }


    return [
        ...(project ? invalidDelivery() : []),
        ...(project ? invalidOrder() : []),
        ...(project ? invalidAccessoryQuantities() : []),
        ...(project ? invalidBilling() : []),
        ...(project ? isPlanImagesDirty() : [])
    ]
}

export const validate = (plan: Plan, project: any): ValidationMessage[] => {
    return [...validatePlan(plan), ...(project ? validateProject(plan, project) : [])]
}

export const hasErrors = (plan: Plan, project: any) =>
    validate(plan, project).some(e => !e.severity || e.severity === "error")
